import { Container, IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { sendCustomerInvite } from '../../../actions/emailActions'
import { createProject } from '../../../actions/projectActions'
import {
  PROJECT_CREATE_RESET,
  PROJECT_CURRENT_STEP,
} from '../../../constants/projectConstants'
import { USER_INVITE_RESET } from '../../../constants/userConstants'
import { useInput } from '../../../hooks/useInput'
import { Context } from '../../../providers/ProjectContextProvider'
import FormContainer from '../../Form/FormContainer'
import ToastMessage from '../../ToastMessage/ToastMessage'
import NotifyUser from '../NotifyUserModal/NotifyUser'
import QuoteProgress from '../QuoteProgress/QuoteProgress'
import UserDetails from '../UserDetails/UserDetails'
import SelectService from './SelectService'
import SelectUser from './SelectUser'

interface Props {
  match: { params: { service: ProjectTypes } }
}

const CreateProject = ({ match }: Props) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const { setShowNotifyUser } = useContext(Context)

  const service = match.params.service

  const {
    value: name,
    bind: bindName,
    error: nameError,
    setError: setNameError,
  } = useInput('')
  const [userSet, setUserSet] = useState(false)
  const [userId, setUserId] = useState('')
  const { value: description, bind: bindDescription } = useInput('')
  const [type, setType] = useState('')
  const [typeError, setTypeError] = useState('')
  const [saveAndGoToDashboard, setSaveAndGoToDashboard] = useState(false)
  const [addCustomerToast, setAddCustomerToast] = useState(false)
  const [stayOnPage, setStayOnPage] = useState(false)

  const { userInfo } = useSelector((state: { userLogin: UserLogin }) => state.userLogin)
  const { loading, error, createdProject, success } = useSelector(
    (state: RootStateOrAny) => state.projectCreate
  )
  const { success: addCustomerSuccess, invitedUser } = useSelector(
    (state: RootStateOrAny) => state.userInvite
  )

  const isAdmin =
    userInfo && userInfo.hasOwnProperty('isAdmin') ? userInfo.isAdmin : false

  useEffect(() => {
    window.scrollTo(0, 0)

    if (!userInfo) {
      history.push('/login?redirect=/createaquote')
    }

    if (success && createdProject) {
      if (stayOnPage) {
        setStayOnPage(false)
      } else {
        if (saveAndGoToDashboard) {
          history.push('/myquality/dashboard')
        } else {
          dispatch({
            type: PROJECT_CURRENT_STEP,
            payload: 2,
          })
          history.push(`/project/edit/${createdProject._id}`)
        }
      }
      dispatch({ type: PROJECT_CREATE_RESET })
    }

    if (error === 'Project name already exists. Please enter a new one.') {
      setNameError(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userInfo,
    success,
    createdProject,
    saveAndGoToDashboard,
    dispatch,
    error,
    setNameError,
    history,
  ])

  useEffect(() => {
    dispatch({
      type: PROJECT_CURRENT_STEP,
      payload: 1,
    })
  }, [dispatch])

  useEffect(() => {
    if (service) {
      setType(service)
    }
  }, [service])

  useEffect(() => {
    if (addCustomerSuccess) {
      setUserId(invitedUser._id)
      setUserSet(true)
      setAddCustomerToast(true)
      dispatch(sendCustomerInvite(invitedUser))

      dispatch({ type: USER_INVITE_RESET })
    }
  }, [dispatch, invitedUser, setUserId, addCustomerSuccess])

  const createNewProject = () => {
    dispatch(
      createProject({
        user: userInfo.isAdmin ? userId : userInfo._id,
        name,
        type,
        description,
        userName: userInfo.firstName + ' ' + userInfo.lastName,
        creator: userInfo.role,
      })
    )
  }

  const formIsReady = () => {
    let ready = true

    if (type === '') {
      ready = false
      setTypeError('Please select a project type.')
    } else {
      setTypeError('')
    }

    if (name === '') {
      ready = false
      setNameError('Please enter a project name.')
    } else {
      setNameError('')
    }

    return ready
  }

  const saveHandler = () => {
    if (formIsReady()) {
      if (userInfo.isAdmin) {
        setStayOnPage(true)
        setShowNotifyUser(true)
        createNewProject()
      } else {
        setSaveAndGoToDashboard(true)
        createNewProject()
      }
    }
  }

  const handleCreateProject = () => {
    if (formIsReady()) {
      createNewProject()
    }
  }

  return (
    <>
      <Container className="create-a-quote">
        <div className="flex space-between m-t-40 m-b-40">
          <h3>
            Create a quote request {userId && <UserDetails userId={userId} />}
          </h3>

          <IconButton
            className="p-l-8 p-r-8"
            onClick={() => history.push('/myquality/dashboard')}
          >
            <Close />
          </IconButton>
        </div>
        <QuoteProgress projectType={type} />

        <FormContainer>
          {isAdmin && !userSet ? (
            <SelectUser userId={userId} setUserId={setUserId} setUserSet={setUserSet} />
          ) : (
            <SelectService
              type={type}
              setType={setType}
              typeError={typeError}
              bindName={bindName}
              nameError={nameError}
              bindDescription={bindDescription}
              saveHandler={saveHandler}
              loading={loading}
              handleCreateProject={handleCreateProject}
              error={error}
            />
          )}
        </FormContainer>
      </Container>

      <NotifyUser />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">New customer added</p>
            <p className="fs14 m-b-0">
              A new customer has been added to your quote request.
            </p>
          </>
        }
        open={addCustomerToast}
        setOpen={setAddCustomerToast}
      />
    </>
  )
}

export default CreateProject
